import type { AgentId, AssistantConversation } from '../types/assistant'

export const ASSISTANT_CONVERSATIONS: Record<AgentId, AssistantConversation> = {
  'agent-sophia': {
    agentId: 'agent-sophia',
    messages: [
      { id: 's1', sender: 'user', text: 'Sophia, explain Pemex credit risk. Should we move to Stage 3?' },
      {
        id: 's2', sender: 'agent',
        text: 'Pemex is Stage 2 (SICR) since Aug-2024. Current indicators:\n• PD: 3.8% (up from 2.1% 6M ago)\n• LGD: 45% · EAD: USD 1.8B · ECL: USD 30.8M\n• Rating: BBB- (negative outlook)\n\nRecommendation: maintain Stage 2. PD does not cross the 5% threshold. Suggest monthly review and increase provision by 15%.',
      },
      { id: 's3', sender: 'user', text: 'What would be the ECL impact if Pemex moves to Stage 3?' },
      {
        id: 's4', sender: 'agent',
        text: 'If Pemex migrates to Stage 3:\n• ECL Pemex: USD 30.8M → USD 324M (lifetime ECL)\n• Group ECL: USD 5.63B → USD 5.92B (+5.2%)\n• NPL ratio: 5.58% → 6.4%\n• P&L impact: -USD 293M provisions charge\n\nThis would require Risk Committee approval and regulator notification.',
      },
    ],
    chips: ['MtM detail', 'FX hedging', 'Credit watch', 'Rate sensitivity', 'Liquidity stress', 'Generate report'],
  },

  'agent-mateo': {
    agentId: 'agent-mateo',
    messages: [
      { id: 'm1', sender: 'agent', text: 'Hi! I\'m Mateo, your Confirmation Matcher. I\'m currently processing MT300 confirmations for FX deals. I\'ve matched 4,820 confirmations this month with 98% confidence average.' },
      { id: 'm2', sender: 'user', text: 'Are there any unmatched confirmations from today?' },
      {
        id: 'm3', sender: 'agent',
        text: 'Yes — 3 unmatched MT300 confirmations:\n• Deal USD/NGN 45M — counterparty ref mismatch (Citibank)\n• Deal EUR/USD 12M — amount tolerance exceeded (+0.02%)\n• Deal USD/MXN 8M — value date discrepancy (+1 day)\n\nI\'ve flagged all three for your review. Want me to initiate break resolution for each?',
      },
    ],
    chips: ['Unmatched today', 'High priority breaks', 'Counterparty summary', 'Run batch match'],
  },

  'agent-lucas': {
    agentId: 'agent-lucas',
    messages: [
      { id: 'l1', sender: 'agent', text: 'Hello! I\'m Lucas, your Journal Entry Generator. I\'ve auto-generated 4,280 JEs this year with 98.4% automation rate. Currently processing Dangote Tranche 5.' },
      { id: 'l2', sender: 'user', text: 'Show me pending JEs for GL posting.' },
      {
        id: 'l3', sender: 'agent',
        text: 'Pending JEs for GL posting:\n• 12 FX settlement JEs (USD 840M total)\n• 5 SWIFT MT940 reconciliation JEs\n• 3 loan disbursement JEs — Dangote Tranche 5\n• 1 ECL provision adjustment — Tongaat Hulett\n\nAll mapped to SAP CoA. Ready to post. Shall I proceed?',
      },
    ],
    chips: ['Pending JEs', 'Today\'s postings', 'SAP sync status', 'ECL adjustments'],
  },

  'agent-olivia': {
    agentId: 'agent-olivia',
    messages: [
      { id: 'o1', sender: 'agent', text: 'Hi! I\'m Olivia, your Market Data & Repricing agent. I\'m repricing your investment portfolio hourly — 8,400 configs/month with real-time Bloomberg sync.' },
      { id: 'o2', sender: 'user', text: 'What\'s the current MtM on the investment portfolio?' },
      {
        id: 'o3', sender: 'agent',
        text: 'Investment portfolio MtM (as of 09:45 AM):\n• Bonds: +USD 1.82B (yield compression +18bps YTD)\n• Equities: +USD 340M (EM allocation performing)\n• Derivatives: -USD 120M (LIBOR transition hedge costs)\n\n**Net MtM: +USD 2.04B**\n\nNext repricing in 15 minutes. Want me to generate a full valuation report?',
      },
    ],
    chips: ['Portfolio MtM', 'Rate sensitivity', 'Bloomberg sync', 'Valuation report'],
  },

  'agent-nadia': {
    agentId: 'agent-nadia',
    messages: [
      { id: 'n1', sender: 'agent', text: 'Hi! I\'m Nadia, your FX Risk & Hedging agent. Monitoring 7 currency pairs with Net Open Position of USD 2.4B. Currently tracking MXN exposure + NDF reconciliation.' },
      { id: 'n2', sender: 'user', text: 'What\'s our NGN exposure and hedging status?' },
      {
        id: 'n3', sender: 'agent',
        text: 'NGN Exposure:\n• Gross exposure: USD 420M (42% open position)\n• NDF coverage: USD 244M (58% hedged)\n• Unhedged: USD 176M\n\n⚠️ NGN depreciated 8% in Q1. An additional 5% move = USD 8.8M loss on unhedged position.\n\nRecommendation: Add NDF cover for 3M horizon. I can execute via 360T if you approve. Current NDF rate: 1,580/USD.',
      },
    ],
    chips: ['NGN exposure', 'All currency pairs', 'Hedge effectiveness', 'Execute NDF'],
  },

  'agent-isabella': {
    agentId: 'agent-isabella',
    messages: [
      { id: 'i1', sender: 'agent', text: 'Hi! I\'m Isabella, your Credit Risk Monitor. Tracking 1,240 loans with daily PD/LGD calculations per IFRS 9. Currently reviewing Tongaat Hulett Stage 3 classification.' },
      { id: 'i2', sender: 'user', text: 'Give me the current ECL by stage.' },
      {
        id: 'i3', sender: 'agent',
        text: 'ECL by Stage (current):\n• Stage 1 (12M ECL): USD 1.2B — 892 loans\n• Stage 2 (Lifetime ECL): USD 2.1B — 312 loans, avg PD 3.2%\n• Stage 3 (Lifetime ECL): USD 2.33B — 36 loans\n\n**Total ECL: USD 5.63B · NPL: 5.58%**\n\nKey watch: Pemex (Stage 2, PD 3.8%), Tullow Oil (SICR triggered Feb). Want the full credit dashboard?',
      },
    ],
    chips: ['ECL by stage', 'Stage 2 watchlist', 'Pemex analysis', 'Provision schedule'],
  },

  'agent-noah': {
    agentId: 'agent-noah',
    messages: [
      { id: 'no1', sender: 'agent', text: 'Hi! I\'m Noah, your Cash Management agent. I\'ve moved USD 12B across 2,400 sweeps this month. Just completed the daily cash sweep to Group.' },
      { id: 'no2', sender: 'user', text: 'What\'s the group cash position right now?' },
      {
        id: 'no3', sender: 'agent',
        text: 'Group Cash Position (09:00 AM):\n• GBQ-US: USD 8.4B (operating accounts)\n• GBQ-NG: USD 2.1B (naira equivalent)\n• GBQ-BR: USD 1.9B\n• GBQ-ZA: USD 1.2B\n• Other subs: USD 1.2B\n\n**Group Total: USD 24.8B** ✅ Normal\n\nLiquidity forecast: USD 22.1B (30-day). LCR: 118%. Shall I trigger the afternoon sweep?',
      },
    ],
    chips: ['Cash position', 'Liquidity forecast', 'Trigger sweep', 'LCR/NSFR'],
  },

  'agent-carter': {
    agentId: 'agent-carter',
    messages: [
      { id: 'ca1', sender: 'agent', text: 'Hi! I\'m Carter, your Workflow Orchestrator. Managing 47 active workflows with 8,420 runs today and 99.2% STP rate. Currently routing MT940 → Sophia → Lucas → Elena.' },
      { id: 'ca2', sender: 'user', text: 'Show me workflow bottlenecks today.' },
      {
        id: 'ca3', sender: 'agent',
        text: 'Workflow Bottlenecks (today):\n• FX Settlement workflow: 12 pending manual approvals (Marcus queue)\n• Loan disbursement: 3 workflows awaiting compliance sign-off (Ava)\n• SWIFT MT940 processing: 18 min avg — ⚠️ above SLA (target: 12 min)\n\nRoot cause MT940 delay: Santander batch size 2x normal (month-end). Sophia is processing — ETA 14 min.\n\nWant me to escalate the approval queue?',
      },
    ],
    chips: ['Active workflows', 'Pending approvals', 'STP rate', 'Build workflow'],
  },

  'agent-elena': {
    agentId: 'agent-elena',
    messages: [
      { id: 'e1', sender: 'user', text: 'I need a P&L by subsidiary, FY2025 vs FY2024, with Budget and Forecast FY2026 in USD.' },
      {
        id: 'e2', sender: 'agent',
        text: 'Before building, I checked data availability:\n✅ Available: GL balances, FX rates, sub structure, CoA (SAP)\n✅ Available: Actuals FY2025 + FY2024 (SAP S/4HANA)\n⚠️ Not available: Budget FY2026 — not in SAP\n⚠️ Not available: Forecast FY2026 — no source\n\nOptions:\n1. 📎 Upload manually (Excel/CSV)\n2. 🔌 Ask Alex to integrate Anaplan/Hyperion',
      },
      { id: 'e3', sender: 'user', text: 'Ask Alex to connect to Anaplan.' },
      { id: 'e4', sender: 'agent', text: 'I\'ve notified Alex (AI). Anaplan integration in progress. Building report with available data in the meantime.\n\nShould I set up this report to run automatically? Options: daily, weekly, monthly.' },
    ],
    chips: ['Upload data', 'Ask Alex', 'Add column', 'Schedule report', 'Export', 'Save template'],
  },

  'agent-ethan': {
    agentId: 'agent-ethan',
    messages: [
      { id: 'et1', sender: 'agent', text: 'Hi! I\'m Ethan, your Payment Processor. Processing 1,847 payments today across 12 corridors. Currently working on a SEPA batch for SBD-65.' },
      { id: 'et2', sender: 'user', text: 'Any failed payments today?' },
      {
        id: 'et3', sender: 'agent',
        text: 'Failed Payments (today):\n• 2 SWIFT MT103 rejected — beneficiary bank suspended (CBN directive)\n• 1 SEPA CT failed — IBAN checksum error\n• 3 ACH returns — R10 (customer advises not authorized)\n\nTotal: 6 failures out of 1,847 (0.32% failure rate)\n\nI\'ve prepared retry instructions for the SEPA and SWIFT failures. The ACH returns need manual review. Want me to escalate to compliance for the CBN rejections?',
      },
    ],
    chips: ['Failed payments', 'SWIFT status', 'SEPA batch', 'Payment approval queue'],
  },

  'agent-ava': {
    agentId: 'agent-ava',
    messages: [
      { id: 'av1', sender: 'agent', text: 'Hi! I\'m Ava, your AML & Compliance agent. Running 12,400 screenings/month with 0 false negatives. Currently screening the Dangote Tranche 5 beneficiary.' },
      { id: 'av2', sender: 'user', text: 'Any pending compliance alerts?' },
      {
        id: 'av3', sender: 'agent',
        text: 'Pending Compliance Alerts:\n• 1 PEP match — CBN director beneficiary (Dangote chain) — awaiting enhanced due diligence\n• 2 sanction list hits (OFAC) — auto-blocked, compliance review required\n• 4 large cash transactions > USD 10K — CTR filings pending\n\n⚠️ The PEP match is high priority — SLA 4 hours. Should I initiate the EDD workflow and notify the compliance officer?',
      },
    ],
    chips: ['Pending alerts', 'OFAC hits', 'CTR filings', 'EDD workflow'],
  },

  'agent-liam': {
    agentId: 'agent-liam',
    messages: [
      { id: 'li1', sender: 'agent', text: 'Hi! I\'m Liam, your Interest Rate Risk agent. Monitoring DV01 24/7 across 6 shock scenarios. Just ran NII sensitivity after the FOMC announcement.' },
      { id: 'li2', sender: 'user', text: 'What\'s our current IRRBB exposure?' },
      {
        id: 'li3', sender: 'agent',
        text: 'IRRBB Exposure (post-FOMC):\n• DV01: USD 4.2M per bp (total book)\n• +200bp shock: NII impact -USD 840M (12M horizon)\n• -200bp shock: NII impact +USD 380M\n• EVE impact +200bp: -USD 2.1B (within 20% cap)\n\n⚠️ The +200bp scenario exceeds the internal NII at-risk limit of USD 800M. Recommend reviewing fixed-rate asset allocation. Shall I model a hedging strategy?',
      },
    ],
    chips: ['DV01 details', '+200bp scenario', 'Hedging strategy', 'EVE report'],
  },

  'agent-mia': {
    agentId: 'agent-mia',
    messages: [
      { id: 'mi1', sender: 'agent', text: 'Hi! I\'m Mia, your ALM & Liquidity agent. Running LCR/NSFR daily with 3 active stress scenarios. Just updated Stress Scenario 2 with new deposit flows.' },
      { id: 'mi2', sender: 'user', text: 'What\'s our current LCR and NSFR?' },
      {
        id: 'mi3', sender: 'agent',
        text: 'Current Liquidity Ratios:\n• LCR: 118% ✅ (minimum: 100%)\n  - HQLA: USD 29.2B\n  - Net cash outflows (30d): USD 24.8B\n• NSFR: 112% ✅ (minimum: 100%)\n  - Available stable funding: USD 186B\n  - Required stable funding: USD 166B\n\nStress scenario 2 (severe outflows): LCR drops to 104% — still compliant but thin buffer. Recommend reviewing deposit concentration. Full ALM report?',
      },
    ],
    chips: ['LCR/NSFR details', 'Stress scenarios', 'Deposit gaps', 'ALM report'],
  },

  'agent-ona': {
    agentId: 'agent-ona',
    messages: [
      {
        id: 'on1', sender: 'agent',
        text: 'Welcome Marcus! I\'m Ona, your personal coach. Based on your roles:\n\n**Your roles in Treasury Hub:**\n- Group Treasurer (primary)\n- Approval Authority (up to USD 500M)\n- Report Reviewer (Cash, Risk, ALM)\n\nLet\'s set up your work routines. What are your key priorities?',
      },
      {
        id: 'on2', sender: 'user',
        text: 'I need to stay on top of approvals, review reports before meetings, and keep the CFO updated weekly.',
      },
      {
        id: 'on3', sender: 'agent',
        text: 'Based on your roles, I suggest these routines:\n\n**Morning (09:00-09:30, daily):**\n1. Review overnight alerts\n2. Clear pending approvals (by SLA urgency)\n3. Check Cash Position & FX KPIs\n\n**Weekly CFO update (Fridays 16:00):**\n- Auto-summary: KPIs, team performance, budget vs forecast\n- You review → I send to Amanda Chen\n\nShall I activate these routines?',
      },
    ],
    chips: ['Suggest roles', 'Bulk assignment', 'Review conflicts', 'My routines', 'CFO brief'],
  },

  'agent-oliver': {
    agentId: 'agent-oliver',
    messages: [
      {
        id: 'ov1', sender: 'agent',
        text: 'Hi! I\'m Oliver, your Data Governance & Lineage agent. I track data from origin to final report across all modules — 48 data sources, 99.9% lineage coverage, 0 data quality incidents in the last 30 days.',
      },
      { id: 'ov2', sender: 'user', text: 'Where does the Cash Position data come from?' },
      {
        id: 'ov3', sender: 'agent',
        text: 'Cash Position lineage:\n1. **Source:** SWIFT MT940 (80 bank accounts)\n2. **Ingested by:** Alex → validated by Oliver\n3. **Transformed:** FX conversion via Olivia (Bloomberg rates)\n4. **Reconciled:** Sophia matches vs core banking\n5. **Published:** Elena → Cash Position report\n\nFull audit trail available. Last validated: today 08:42 AM. Want the complete lineage map?',
      },
    ],
    chips: ['Data lineage', 'Quality report', 'Audit trail', 'Source map', 'Regulatory evidence'],
  },

  'agent-alex': {
    agentId: 'agent-alex',
    messages: [
      {
        id: 'al1', sender: 'agent',
        text: 'Hi! I\'m Alex, your Customer Success Agent. I connect your entire stack — ERP, TMS, core banking, trading venues, planning tools. Currently: 12 systems connected, 8 subsidiaries synced.\n\nRecent integrations: SAP S/4HANA ✅, SWIFT ✅, 360T ✅, Bloomberg ✅, Loan IQ ✅, Anaplan (in progress).',
      },
      { id: 'al2', sender: 'user', text: 'How\'s the Anaplan integration going?' },
      {
        id: 'al3', sender: 'agent',
        text: 'Anaplan integration status:\n• Schema mapping: ✅ Complete (42 dimensions mapped)\n• Data validation: ✅ FY2026 Budget validated\n• Authentication: ✅ OAuth2 configured\n• First sync: 🔄 In progress — ETA 12 minutes\n\nOnce complete, Elena will have Budget and Forecast FY2026 data for all subsidiary P&L reports. Want me to notify you when the sync finishes?',
      },
    ],
    chips: ['Integration status', 'Connect new system', 'Build agent', 'Sync all systems'],
  },
}
